import React from "react";
import { Reveal } from "../motion/space-primitives";

// What Nodefleet's security practice covers. Icons use Font Awesome 6 (loaded
// globally via CDN in public/index.html), styled in the brand green.
const SERVICES = [
  {
    icon: "fa-file-contract",
    title: "Smart Contract Audits",
    desc: "Solidity & EVM review — bridges, tokens, DeFi and access control, line by line.",
  },
  {
    icon: "fa-diagram-project",
    title: "Protocol & Consensus",
    desc: "BFT engines, validator clients, Cosmos SDK and Substrate app-chains.",
  },
  {
    icon: "fa-bridge",
    title: "Bridge & Cross-Chain",
    desc: "Lock / mint flows, message passing and the signer trust models behind them.",
  },
  {
    icon: "fa-layer-group",
    title: "Layer 2 & Rollups",
    desc: "Sequencers, fraud & validity proofs, finalization and data availability.",
  },
  {
    icon: "fa-chart-line",
    title: "DeFi & Economic Review",
    desc: "Lending, AMMs and yield — accounting, custody and liveness invariants.",
  },
  {
    icon: "fa-shield-halved",
    title: "Infrastructure Hardening",
    desc: "Node operations, key management, deployment discipline and monitoring.",
  },
];

// The researchers behind the assessments (photos reused from the team assets).
const AUDITORS = [
  {
    name: "Lowell Abbott",
    role: "Co-Founder & Advisor",
    photo: "/assets/team/lowell.png",
  },
  {
    name: "Juan Carlos Abreu",
    role: "Full-Stack Engineer",
    photo: "/assets/team/juancarlos.jpeg",
  },
  {
    name: "Jonathan Maria",
    role: "SRE",
    photo: "/assets/team/jhonathan.jpg",
  },
  {
    name: "Kael Abbott",
    role: "Co-Founder & CEO",
    photo: "/assets/team/kael.png",
  },
];

// Protocols Nodefleet has security-assessed (logos from the audited projects).
const PROTOCOLS = [
  { name: "Orochi Network", logo: "/assets/icons/orochi-logo.svg" },
  { name: "Canopy", logo: "/assets/icons/canopy-symbol.png" },
  { name: "Linea", logo: "/assets/icons/linea-logo.png" },
  { name: "Dypius", logo: "/assets/icons/dypius-logo.png" },
  { name: "Mantle", logo: "/assets/icons/mantle-logo.png" },
  { name: "infiniFi", logo: "/assets/icons/infinifi-logo.png" },
  { name: "Lava Network", logo: "/assets/icons/lava-logo.png" },
  { name: "Passage", logo: "/assets/icons/passage-logo.svg" },
  { name: "Story Protocol", logo: "/assets/icons/story-logo.png" },
  { name: "Pocket Network", logo: "/assets/icons/pokt-symbol.svg" },
  { name: "dYdX", logo: "/assets/icons/dydx-logo.svg" },
  { name: "wPOKT", logo: "/assets/icons/pokt-symbol.svg" },
];

const SecurityHighlights = () => {
  return (
    <>
      {/* Services */}
      <section className="w-full px-8 py-20 max-sm:px-6 max-sm:py-14 lg:px-20">
        <Reveal as="h2" className="text-4xl font-bold text-white max-sm:text-3xl">
          Security <span className="text-brand-gradient italic">services</span>
        </Reveal>
        <Reveal
          delay={0.08}
          className="mt-4 max-w-2xl text-lg text-gray-400 max-sm:text-base"
        >
          From smart contracts to consensus engines — full-stack security for the
          teams building Web3 infrastructure.
        </Reveal>

        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s) => (
            <div
              key={s.title}
              className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 transition-colors hover:border-primary/30"
            >
              <div className="grid h-12 w-12 place-items-center rounded-xl border border-primary/30 bg-primary/10">
                <i className={`fa-solid ${s.icon} text-xl text-primary`} />
              </div>
              <h3 className="mt-5 text-xl font-bold text-white">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-gray-400">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Protocols Assessed */}
      <section className="w-full px-8 py-20 max-sm:px-6 max-sm:py-14 lg:px-20">
        <Reveal as="h2" className="text-4xl font-bold text-white max-sm:text-3xl">
          Protocols <span className="text-brand-gradient italic">assessed</span>
        </Reveal>
        <Reveal
          delay={0.08}
          className="mt-4 max-w-2xl text-lg text-gray-400 max-sm:text-base"
        >
          Layer 1s, rollups, bridges and DeFi protocols that trusted Nodefleet to
          review their code.
        </Reveal>

        <div className="mt-12 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {PROTOCOLS.map((p) => (
            <div
              key={p.name}
              className="flex h-24 items-center justify-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] px-6 transition-colors hover:border-primary/30"
            >
              <img
                src={p.logo}
                alt={p.name}
                loading="lazy"
                className="max-h-9 max-w-[70%] object-contain opacity-70 grayscale transition-all duration-300 hover:opacity-100 hover:grayscale-0"
              />
            </div>
          ))}
        </div>
      </section>

      {/* Auditors */}
      <section className="w-full px-8 py-20 max-sm:px-6 max-sm:py-14 lg:px-20">
        <Reveal as="h2" className="text-4xl font-bold text-white max-sm:text-3xl">
          The <span className="text-brand-gradient italic">auditors</span> behind
          the research
        </Reveal>
        <Reveal
          delay={0.08}
          className="mt-4 max-w-2xl text-lg text-gray-400 max-sm:text-base"
        >
          Protocol engineers and infrastructure architects who break systems
          before attackers do.
        </Reveal>

        <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {AUDITORS.map((a) => (
            <div
              key={a.name}
              className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 transition-colors hover:border-primary/30"
            >
              <img
                src={a.photo}
                alt={a.name}
                loading="lazy"
                className="h-16 w-16 rounded-full object-cover object-center ring-2 ring-white/10"
              />
              <h3 className="mt-4 font-semibold text-white">{a.name}</h3>
              <p className="text-sm text-primary">{a.role}</p>
            </div>
          ))}
        </div>
      </section>
    </>
  );
};

export default SecurityHighlights;
